import { type Entitlement, graceRemainingMs, PAST_DUE_GRACE_DAYS, SubscriptionStatus } from '@parsleyai/billing'
import type { SubscriptionCardState } from '@parsleyai/ui'
import { BillingPhase, type BillingState } from '@/store/userData/billing/types'

/**
 * How long an absent billing document reads as "still provisioning" before it's called a failure.
 * Deliberately much shorter than the server-side alert on the same condition, because someone is watching.
 */
export const PROVISIONING_GRACE_MS = 30_000

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Map the billing slice onto the card's state. Two states can't be read off `status`: `cancel_pending`
 * is `active` + `cancelAtPeriodEnd`, and `grace_expired` is `past_due` past {@link PAST_DUE_GRACE_DAYS}.
 */
export function toCardState(state: BillingState, entitlement: Entitlement, nowMs: number): SubscriptionCardState {
  if (state.phase === BillingPhase.Loading) return 'loading'

  if (state.phase === BillingPhase.Provisioning || !state.subscription) {
    // `none` is not a shopping state — subscriptions are provisioned automatically, so no document
    // after the grace window means provisioning failed. The card's copy says so.
    const waitedMs = nowMs - (state.provisioningSinceMs ?? nowMs)
    return waitedMs > PROVISIONING_GRACE_MS ? 'none' : 'provisioning'
  }

  const { status, cancelAtPeriodEnd } = state.subscription

  switch (status) {
    case SubscriptionStatus.Trialing:
      return 'trialing'
    case SubscriptionStatus.Active:
      return cancelAtPeriodEnd ? 'cancel_pending' : 'active'
    case SubscriptionStatus.PastDue:
      // The grace line comes from `entitlement`, not a second calculation here.
      return entitlement.active ? 'past_due' : 'grace_expired'
    case SubscriptionStatus.Paused:
      return 'paused'
    case SubscriptionStatus.Canceled:
      return 'canceled'
    default:
      // unpaid / incomplete / incomplete_expired — three Stripe statuses whose next step for the
      // user is identical, so they share one screen rather than three near-identical ones.
      return 'terminal'
  }
}

/**
 * Whole days left in the past-due grace window. Bounded by the shared helper, so the figure can
 * never exceed its own denominator — "223 of 14 grace days left" was a future anchor, unclamped.
 */
export function graceDaysRemaining(pastDueSinceMs: number, nowMs: number): number {
  return Math.ceil(graceRemainingMs(pastDueSinceMs, nowMs) / DAY_MS)
}

/** Whole days until a trial ends, floored at 0. */
export function trialDaysRemaining(trialEndMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((trialEndMs - nowMs) / DAY_MS))
}
